import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useUiPreferences } from "../contexts/UiPreferencesContext";
import "./UserSettingsModal.css";

const TITLE_ID = "user-settings-title";
const THEME_LABEL_ID = "user-settings-theme-label";
const COLOR_VISION_LABEL_ID = "user-settings-color-vision-label";
const COLOR_VISION_HELP_ID = "user-settings-color-vision-help";

const THEME_OPTIONS = [
  { value: "system", label: "Sistema" },
  { value: "light", label: "Claro" },
  { value: "dark", label: "Escuro" },
];

const COLOR_VISION_OPTIONS = [
  { value: "off", label: "Desligado" },
  { value: "protanopia", label: "Protanopia" },
  { value: "deuteranopia", label: "Deuteranopia" },
  { value: "tritanopia", label: "Tritanopia" },
];

function UserSettingsModal({ open, onClose, onAlterarSenha, onSair }) {
  const { themePreference, setThemePreference, colorVision, setColorVision } =
    useUiPreferences();
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const handleAlterarSenha = () => {
    onClose();
    onAlterarSenha();
  };

  const handleSair = () => {
    onClose();
    onSair();
  };

  const handleThemeChange = (event) => {
    setThemePreference(event.target.value);
  };

  const handleColorVisionChange = (event) => {
    setColorVision(event.target.value);
  };

  return (
    <Dialog
      id="user-settings-dialog"
      open={open}
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      hideBackdrop={false}
      maxWidth={false}
      transitionDuration={reduceMotion ? 0 : { enter: 0, exit: 200 }}
      BackdropProps={{
        transitionDuration: reduceMotion ? 0 : 300,
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
        },
      }}
      PaperProps={{
        className: reduceMotion ? undefined : "user-settings-modal-paper",
        sx: {
          position: "absolute",
          top: { xs: 56, sm: 64 },
          right: { xs: 8, sm: 16 },
          left: "auto",
          m: 0,
          width: { xs: "min(100% - 16px, 340px)", sm: 340 },
          maxWidth: 360,
          bgcolor: "background.paper",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle
        id={TITLE_ID}
        tabIndex={-1}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1.5,
        }}
      >
        Configurações
        <IconButton aria-label="Fechar" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <FormControl
          component="fieldset"
          variant="standard"
          sx={{ display: "block", mb: 2.5 }}
        >
          <FormLabel id={THEME_LABEL_ID} component="legend">
            Tema
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby={THEME_LABEL_ID}
            name="tema"
            value={themePreference}
            onChange={handleThemeChange}
            sx={{ flexWrap: "wrap", mt: 0.5 }}
          >
            {THEME_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio size="small" />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <FormControl component="fieldset" variant="standard" sx={{ display: "block" }}>
          <FormLabel id={COLOR_VISION_LABEL_ID} component="legend">
            Modo de cores
          </FormLabel>
          <Typography
            id={COLOR_VISION_HELP_ID}
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, mb: 0.5 }}
          >
            Ajuda quem tem dificuldade em distinguir cores.
          </Typography>
          <RadioGroup
            aria-labelledby={COLOR_VISION_LABEL_ID}
            aria-describedby={COLOR_VISION_HELP_ID}
            name="modo-de-cores"
            value={colorVision}
            onChange={handleColorVisionChange}
          >
            {COLOR_VISION_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio size="small" />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1,
          px: 3,
          py: 2,
          "& > :not(style) ~ :not(style)": {
            ml: 0,
          },
        }}
      >
        <Button variant="outlined" onClick={handleAlterarSenha} fullWidth>
          Alterar senha
        </Button>
        <Button variant="text" color="inherit" onClick={handleSair} fullWidth>
          Sair
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UserSettingsModal;
